import React, {useState} from 'react';
import {Checkbox, Modal, Select, Table, theme} from "antd";
import {ColumnsType} from "antd/es/table";
import {QueryKey, useQueryClient} from "@tanstack/react-query";
import {TB_STORAGE_OPTION} from "./data/StorageWizMgrData";
import {useCommonUseMutation, useCommonUseQuery} from "../../../../../Common/hooks/rest/ReacQueryHook";
import {formatDate} from "../../../../../Common/util/DateUtil";
import {procAdminService} from "../../../../../Common/util/ProcService";
import {CheckboxChangeEvent} from "antd/es/checkbox";

const StorageInfra: React.FC = () => {
    const { token: { colorBgContainer },} = theme.useToken();
    const queryClient = useQueryClient();
    const queryKey:QueryKey =   [`StorageInfra`];

    const serviceCode:string       =   process.env.REACT_APP_SERVICE_NAME as string;

    const mutateUpdate = useCommonUseMutation<any>(queryKey, queryClient
        , procAdminService);

    const { data, isFetching} = useCommonUseQuery<TB_STORAGE_OPTION[]>(
        queryKey,
        () =>procAdminService<TB_STORAGE_OPTION[]>("post", `${serviceCode}ADS14`, null));

    const [isCompressed, setIsCompressed]= useState<boolean|null>(null);
    const [isEncryptionEnabled, setIsEncryptionEnabled]= useState<boolean|null>(null);
    const [showConfirmBox, setShowConfirmBox] = useState<boolean>(false);
    const [infraType, setInfraType] = useState<string|null>();


    const onChangeCompressed = (e:CheckboxChangeEvent, record:TB_STORAGE_OPTION) => {

        setIsCompressed(e.target.checked);

        let isEncryptionEnabledParam:string = '';
        if (isEncryptionEnabled)
        {
            isEncryptionEnabledParam = isEncryptionEnabled ? '1' : '0'
        }
        else
        {
            isEncryptionEnabledParam = record.isEncryptionEnabled;
        }

        const request:TB_STORAGE_OPTION = {
            id:record.id,
            activeClassName:record.activeClassName,
            isCompressed:e.target.checked ? '1' : '0',
            isEncryptionEnabled: isEncryptionEnabledParam,
        }

        confirmBox("파일 압축 설정", request);

    };

    const onChangeEncryptionEnabled = (e:CheckboxChangeEvent, record:TB_STORAGE_OPTION) => {

        setIsEncryptionEnabled(e.target.checked);
        let isCompressedParam:string = '';
        if (isCompressed)
        {
            isCompressedParam = isCompressed ? '1' : '0'
        }
        else
        {
            isCompressedParam = record.isCompressed;
        }


        const request:TB_STORAGE_OPTION = {
            id:record.id,
            activeClassName:record.activeClassName,
            isCompressed:isCompressedParam,
            isEncryptionEnabled:e.target.checked ? '1' : '0'
        }

        confirmBox("파일 암호화여부 설정", request);

    };

    const handleChange = (value:string, record:TB_STORAGE_OPTION) => {

        const request:TB_STORAGE_OPTION = {
            id:record.id,
            activeClassName:value,
            isCompressed:record.isCompressed,
            isEncryptionEnabled:record.isCompressed
        }

        Modal.confirm({
            title: `스토리지 인프라 설정을 수정하시겠습니까?`,
            content: "주의 : 시스템에 영향이 미칠 수 있습니다.",
            open: showConfirmBox,
            okText: "확인",
            cancelText: "취소",
            onOk() {
                setShowConfirmBox(false);
                const updateParam = { method: 'post', serviceCode: `${serviceCode}ADU04`, requestBody:request }
                return mutateUpdate.mutateAsync(updateParam)
                    .catch(error => {
                        console.error(error);
                        setInfraType(record.activeClassName);

                    }).finally(() => {
                        setInfraType(value);
                    });
            },
            onCancel() {
                setInfraType(record.activeClassName);
                setShowConfirmBox(false);
            },
        });

    };

    function confirmBox(jobName:string, request:TB_STORAGE_OPTION)
    {

        Modal.confirm({
            title: `${jobName}을 수정하시겠습니까?`,
            content: "주의 : 시스템에 영향이 미칠 수 있습니다.",
            open: showConfirmBox,
            okText: "확인",
            cancelText: "취소",
            onOk() {
                setShowConfirmBox(false);
                const updateParam = { method: 'post', serviceCode: `${serviceCode}ADU04`, requestBody:request }
                return mutateUpdate.mutateAsync(updateParam)
                    .catch(error => {
                        console.error(error);

                    }).finally(() => {
                    });
            },
            onCancel() {
                setShowConfirmBox(false);
            },
        });
    }


    const columns: ColumnsType<TB_STORAGE_OPTION> = [
        {
            title: 'Index',
            dataIndex: 'id',
            key: 'id',
            render: (id: number) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {id}
                </div>
            ,
        },
        {
            title: '클라우드 종류',
            dataIndex: 'activeClassName',
            key: 'activeClassName',
            render: (text: string, record:TB_STORAGE_OPTION) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Select
                        defaultValue={text}
                        style={{ width: 200 }}
                        value={infraType||record.activeClassName}
                        onChange={(value)=>{handleChange(value, record)}}
                        options={[
                            { value: 'LOCALTASK', label: 'Hard Disk' },
                            { value: 'SMBTASK', label: 'SMB' },
                            { value: 'FTPTASK', label: 'FTP' },
                            { value: 'AMAZONTASK', label: 'Amazon S3' },
                            { value: 'ORACLETASK', label: 'Oracle Object Storage' },
                        ]}
                    />
                </div>
            ,
        },
        {
            title: '보안저장',
            dataIndex: 'isEncryptionEnabled',
            key: 'isEncryptionEnabled',
            render: (text: string, record:TB_STORAGE_OPTION) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkbox checked={isEncryptionEnabled||text === '1' ? true : false} value={isEncryptionEnabled} onChange={(e:CheckboxChangeEvent)=>{onChangeEncryptionEnabled(e, record)}}/>
                </div>
        },
        {
            title: '압축저장',
            dataIndex: 'isCompressed',
            key: 'isCompressed',
            render: (text: string, record:TB_STORAGE_OPTION) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                   <Checkbox checked={isCompressed||text === '1' ? true : false} value={isCompressed} onChange={(e:CheckboxChangeEvent)=>{onChangeCompressed(e, record)}}/>
                </div>
        },
        {
            title: '생성일자',
            key: 'firstRegTime',
            dataIndex: 'firstRegTime',
            render: (text: string) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {formatDate(text)}
                </div>
            ,
        },
        {
            title: '수정일자',
            key: 'lastRegTime',
            dataIndex: 'lastRegTime',
            render: (text: string) =>
                <div style={{ paddingBottom: '9px', margin:'0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {formatDate(text)}
                </div>
            ,
        },

    ];

    return (
        <>
        <Table columns={columns}
               dataSource={data}
               rowKey="id"
               loading={isFetching}
        />
        </>
    );
};

export default StorageInfra;
